import {
  GET_MERCHANTS_SUCCESS,
  GET_MERCHANT_SUCCESS,
  ResolvedAction,
  SET_MERCHANT_LIVE_UPDATES_STATE,
} from 'actions/merchant'
import { RESET_DB } from 'actions/orm'
import type { LiveUpdatesState } from 'api/liveUpdates.types'
import { merchantsApi } from 'api/merchants'
import { attr, Model, ModelType } from 'redux-orm'

import { ImageUrlsT } from 'utilities/types'
import { RedemptionProvider } from './RedemptionProvider'

export type OrderingProvider = 'toast' | 'olo' | 'deliverect' | 'square' | 'qu'

// One published store release of a merchant's branded app.
export type PublishedAppVersion = {
  // Thanx's packing of the semver into ONE sortable integer:
  // major * 1_000_000 + minor * 1_000 + patch, so 63.0.0 is 63_000_000.
  // Every comparison runs on this. `version` below is display only, and
  // comparing it as a string gets '54.1.1' against '63.0.0' wrong the moment
  // a field goes double-digit.
  version_code: number
  // The human semver, e.g. '54.1.1'. Display only.
  version: string
}

// Both platform keys are ALWAYS present on the payload. A platform with no
// published release is `null`, and a merchant with no branded app at all is
// `null` on both — the common case, not an edge case (429 of 759 production
// merchants today).
export type PublishedAppVersions = {
  ios: PublishedAppVersion | null
  android: PublishedAppVersion | null
}

export interface Fields {
  id: number
  hashid: string
  app_primary_image_urls: ImageUrlsT
  app_secondary_image_urls: ImageUrlsT
  automatic_capable: boolean
  average_check_size: number
  branded_app: boolean
  exclusive_deals: boolean
  cover_image_urls: ImageUrlsT
  custom_campaigns_enabled: boolean
  handle: string
  intro_description: string
  logo_image_urls: ImageUrlsT
  reward_icon_urls: ImageUrlsT | null
  loyalty_description: string
  primary_color: string | null
  merchant_type: string
  name: string
  nickname: string
  ordering_enabled: boolean
  point_of_sale: string
  processor: string
  purchase_volume: number
  state: string
  user_count: number
  live_at: string
  support_owner: 'customer_success' | 'support'
  online_only: boolean
  app_ordering_enabled: boolean
  web_ordering_enabled: boolean
  ordering_provider?: OrderingProvider | null
  // Optional/transitional: nexus#186 ships the field. Until that deploys, the
  // API response omits it and the value is undefined at runtime — gating
  // logic must explicitly check `=== false` rather than truthiness.
  olo_promotions_enabled?: boolean
  // Optional/transitional: nexus LOY-605 ships the field. Until that deploys,
  // the API omits it; read it through useCustomerImportRowLimit, which falls
  // back to 100,000.
  customer_import_row_limit?: number
  supports_integrated_ordering: boolean
  supports_ordering_management: boolean
  supports_revenue_centers?: boolean
  supports_service_charges?: boolean
  supports_time_based_menu_visibility?: boolean
  supports_loyalty_integration: boolean
  loyalty_integration_partner:
    | 'toast'
    | 'revel'
    | 'fishbowl'
    | 'northstar'
    | null
  custom_onboarding_enabled: boolean
  loyalty_transition_settings: {
    eligible: boolean
    type: 'phone' | 'email' | 'license'
  }
  support_managed_by: 'thanx' | 'merchant'
  redemption_provider: RedemptionProvider | null
  all_redemption_providers: RedemptionProvider[]
  has_direct_integration_provider: boolean
  has_multi_pos: boolean
  supports_instore_scanning: boolean
  instore_scanning_enabled: boolean
  feedback_enabled: boolean
  live_updates_state: LiveUpdatesState
  // DATA-5120: gates the post-order feedback email channel. Optional/transitional —
  // until nexus PR-A deploys, the API response omits the field and the value is
  // undefined at runtime; the slice falls back to false.
  allow_post_order_feedback_email?: boolean
  // Optional/transitional: nexus thanx/nexus#1938 (open) adds this field with a
  // server-side default of `true`. Until that deploys, the API response omits
  // it and it is `undefined` at runtime — every reader must treat undefined as
  // "true" (the documented default, and the real-world state today: written
  // feedback collection is on for every merchant), NOT as "not loaded".
  allow_written_feedback_collection?: boolean
  // FR-55. Optional/transitional: nexus thanx/nexus#1976 adds these two fields
  // with server-side defaults (`order_feedback_reply_mode` defaults to
  // 'user'; `order_feedback_reply_address` is nullable). Until that deploys,
  // the API response omits them and they are `undefined` at runtime — treat
  // an absent mode as 'user' and an absent/null address as blank, never as
  // "not loaded".
  order_feedback_reply_mode?: 'user' | 'fixed'
  order_feedback_reply_address?: string | null
  // FR-56/FR-57. Optional/transitional: nexus thanx/nexus#1977 adds these two
  // fields with server-side defaults (`order_feedback_call_the_store_enabled`
  // defaults to `true`; `order_feedback_acknowledgment_message` defaults to
  // 'Thanks for your feedback.'). Until that deploys, the API response omits
  // them and they are `undefined` at runtime — default with `??`, never `||`,
  // since `false` is a meaningful value for the first field.
  order_feedback_call_the_store_enabled?: boolean
  order_feedback_acknowledgment_message?: string
  // The live store releases of the merchant's branded app, used to flag the
  // guest-app-version dependency that FR-56/FR-57's two settings carry.
  // Exposed on the FULL merchant payload only — the `:picker` and `:minimal`
  // serializations omit it entirely. Optional/transitional on top of that:
  // nexus `13a0e8b5a8` adds it, and until that deploys the field is
  // `undefined` at runtime. Undefined means "not known yet", NOT "no app" —
  // never read an absent object as a published version of zero.
  published_app_versions?: PublishedAppVersions
  earn_loyalty_type: 'credit_card' | 'check_in'
  supports_credit_card_loyalty_type: boolean
  gift_cards_enabled: boolean
  pos_enrollment_setting?: 'never' | 'on_request' | 'always'
  // null = no selection yet OR "No expiration"; disambiguate via _set below.
  granted_reward_retire_after_days?: number | null
  granted_reward_retire_after_days_set?: boolean
  has_granted_reward_backlog?: boolean
  // Write-only opt-in on the merchant PATCH; never returned from GET.
  apply_to_granted_rewards?: boolean
}

// @ts-ignore
export default class Merchant extends Model<typeof Merchant, Fields> {
  static modelName: string = 'Merchant'

  static get fields(): any {
    return {
      id: attr(),
      hashid: attr(),
      name: attr(),
      handle: attr(),
      state: attr(),
      processor: attr(),
      point_of_sale: attr(),
      average_check_size: attr(),
      purchase_volume: attr(),
      automatic_capable: attr(),
      user_count: attr(),
      all_redemption_providers: attr(),
      logo_image_urls: attr(),
      cover_image_urls: attr(),
      app_primary_image_urls: attr(),
      loyalty_description: attr(),
      pos_enrollment_setting: attr(),
      granted_reward_retire_after_days: attr(),
      granted_reward_retire_after_days_set: attr(),
      has_granted_reward_backlog: attr(),
    }
  }

  /* eslint-disable no-use-before-define */
  static reducer(
    action: ResolvedAction | { type: typeof RESET_DB },
    // @ts-ignore
    Merchant: ModelType<Merchant>
  ) {
    /* eslint-disable no-use-before-define */

    // Handle RTK Query actions with matcher functions (before switch statement)
    if (
      merchantsApi.endpoints?.updateMerchant?.matchFulfilled &&
      typeof merchantsApi.endpoints.updateMerchant.matchFulfilled ===
        'function' &&
      merchantsApi.endpoints.updateMerchant.matchFulfilled(action)
    ) {
      const {
        payload: { merchant },
      } = action as { payload: { merchant: Fields } }
      Merchant.upsert(merchant)
      return
    }

    switch (action.type) {
      case GET_MERCHANTS_SUCCESS: {
        const getMerchantsAction = action as {
          type: typeof GET_MERCHANTS_SUCCESS
          payload: { data: { merchants: Fields[] } }
        }
        getMerchantsAction.payload.data.merchants.forEach(
          (merchant: Fields) => {
            Merchant.upsert(merchant)
          }
        )
        break
      }
      case GET_MERCHANT_SUCCESS: {
        const getMerchantAction = action as {
          type: typeof GET_MERCHANT_SUCCESS
          payload: { data: { merchant: Fields } }
        }
        Merchant.upsert(getMerchantAction.payload.data.merchant)
        break
      }
      case SET_MERCHANT_LIVE_UPDATES_STATE: {
        const {
          payload: { merchantId, liveUpdatesState },
        } = action
        const merchant = Merchant.withId(merchantId)
        if (merchant) {
          merchant.update({ live_updates_state: liveUpdatesState })
        }
        break
      }
      case RESET_DB:
        Merchant.all().delete()
        break
      default:
        break
    }
  }
}
